import React, { useRef, useEffect } from 'react';
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";
import {getFormattedEnvName, isMobileApp} from "../../../Utils/misc/envNameUtils.ts";
import { handleLinkClick } from "../../../Utils/misc/openLink.ts";

interface IntroMessageProps {}

const IntroMessage: React.FC<IntroMessageProps> = () => {
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment, vscode } = environmentObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const modalRef = useRef<HTMLDivElement>(null);

    const handleClose = () => {
        setDynamicStateObj({ visiblePopup: null });
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
            setDynamicStateObj({ visiblePopup: null });
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getEnvironmentSpecificContent = () => {
        if (getFormattedEnvName(environment) === "Web" || environment === "chrome" || isMobileApp(environment)) {
            return {
                title: "Welcome to Onuro Chat!",
                description: "Onuro is your versatile AI assistant, here to assist you with anything you need.",
                tips: [
                    "Try the modes selector at the bottom",
                    "Ask the AI to search the internet, find flights, find food, and more!",
                    "Upload images, videos, or files of any kind",
                    "Give the AI an image and tell it to edit it... Or multiple images at the same time",
                    "Click the settings menu at the top to customize your experience"
                ]
            };
        } else if (environment.includes("jetbrains") || environment === "vscode") {
            return {
                title: "Welcome to Onuro Code!",
                description: "Onuro turns your JetBrains IDE into an AI powered IDE.",
                tips: [
                    <>Tap or hold <span className="font-bold">option/alt</span> + <span className="font-bold">x</span> to trigger autocomplete suggestions</>,
                    "Try the modes selector at the bottom",
                    "Click the folder at the top left to easily control known context",
                    "Click the settings menu at the top to customize your experience"
                ]
            };
        }

        // Default content
        return {
            title: "Welcome to Onuro!",
            description: "Onuro is your versatile AI assistant, here to assist you with anything you need.",
            tips: []
        };
    };

    const content = getEnvironmentSpecificContent();

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50">
            <div ref={modalRef} className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-[98%] sm:w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 max-w-2xl overflow-hidden border border-neutral-300/50 dark:border-neutral-700/50 relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-600"></div>
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-400 scrollbar-track-neutral-200 dark:scrollbar-thumb-neutral-600 dark:scrollbar-track-neutral-800">
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4">
                        {content.title}
                    </h2>
                    <div className="mb-6">
                        <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                            {content.description}
                        </p>
                    </div>
                    {content.tips.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-200 mb-4">
                                Quick Tips:
                            </h3>
                            <div className="space-y-3">
                                {content.tips.map((tip, index) => (
                                    <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-700/30 border border-neutral-200 dark:border-neutral-600/30">
                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center mt-0.5">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span className="text-neutral-700 dark:text-neutral-200 leading-relaxed">{tip}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 border-t border-neutral-300/50 dark:border-neutral-600/50 pt-4">
                        <p>
                            For a comprehensive walkthrough of our feature guide, please visit{' '}
                            <a
                                href={environment.includes("jetbrains") || environment === "vscode" ? "https://www.onuro.ai/docs/code" : "https://www.onuro.ai/docs/chat"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 underline transition-colors"
                                onClick={(e) => handleLinkClick(e, environment, vscode, environment.includes("jetbrains") || environment === "vscode" ? "https://www.onuro.ai/docs/code" : "https://www.onuro.ai/docs/chat")}
                            >
                                here
                            </a>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntroMessage;